import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Mail, MessageSquare, Phone, Plus, RefreshCw, ShieldCheck, Upload, Users } from "lucide-react";
import { bookingVendorSave } from "@shared/schemas/automation";
import { evaluation, parseInput } from "@shared/validation/issues";
import { validateBookingVendor } from "@shared/validation/automation";
import { errorMessage } from "@web/lib/api";
import { fmtDate, fmtStamp } from "@web/lib/format";
import { useFormValidation } from "@web/lib/formValidation";
import { uploadCertificate } from "@web/lib/queries-automation";
import { bookingVendorApi, bookingVendorsQuery, type BookingVendor } from "@web/lib/queries-vendors";
import { VendorPickerSheet } from "./VendorPicker";
import { attachmentsQuery } from "@web/lib/queries-attachments";
import { bookingReviewQuery } from "@web/lib/queries-contracts";
import type { BookingDetail } from "@web/lib/queries";
import { InsuranceSheet } from "./FilesTab";
import {
  BottomSheet, Button, Card, EmptyState, FilePicker, FormGrid, Inline, NavBarAction, Picker, Region,
  Row, RowGroup, Skeleton, Stack, StatusChip, Text, TextArea, TextField, UnsavedChanges,
  ValidationSummary, Workspace, useToast,
} from "@web/ui";

/** Booking › Insurance & vendors (SPEC §6.1 item 8, §7.9).
 *
 * The certificate and the vendor list answer the same question — can this event
 * actually go ahead — so they live on one tab. Editing the status, carrier and
 * policy number happens in the Review sheet, which guards on the booking's source
 * hash; this tab is where the document arrives and where the vendor list lives. */
const COI_TONES = { received: "success", waived: "neutral", overdue: "danger", pending: "warn" } as const;
const coiTone = (status: string) => COI_TONES[status as keyof typeof COI_TONES] ?? "warn";
const statusLabel = (status: string) => status === "received" ? "Received" : status === "waived" ? "Waived" : status === "overdue" ? "Overdue" : "Pending";

export function InsuranceTab({ booking, readOnly }: { booking: BookingDetail; readOnly: boolean }) {
  const toast = useToast();
  const review = useQuery(bookingReviewQuery(booking.id));
  const files = useQuery(attachmentsQuery({ entityType: "booking", entityId: booking.id }));
  const vendors = useQuery(bookingVendorsQuery(booking.id));
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState<BookingVendor | null>(null);
  const [adding, setAdding] = useState(false);
  const [picking, setPicking] = useState(false);
  const [editingInsurance, setEditingInsurance] = useState(false);

  const insurance = review.data?.insurance;
  const required = insurance ? insurance.required === 1 : true;
  const status = insurance?.status ?? "pending";

  /** Taking a fresh copy of the directory's details. Deliberately something a
   * person does: the row is the record of who was called on the day. */
  async function resync(vendor: BookingVendor) {
    try { await bookingVendorApi.resync(booking.id, vendor.id); toast.success(`${vendor.name} updated from the directory`); }
    catch (error) { toast.error(errorMessage(error)); }
  }

  async function upload(file: File) {
    setUploading(true);
    try { await uploadCertificate(booking.id, file); await review.refetch(); toast.success("Certificate uploaded"); }
    catch (error) { toast.error(errorMessage(error)); }
    finally { setUploading(false); }
  }

  return <Workspace variant="record">
    <Region area="aside" label="Certificate of insurance">
      <Stack gap={3}>
        <Card><Stack gap={3}>
          <Inline justify="between" align="start" wrap>
            <Stack gap={1}>
              <Text as="h2" variant="title">Certificate of insurance</Text>
              <Text variant="caption" tone="secondary">
                {!required ? "This booking does not require insurance."
                  : insurance?.dueDate ? `Due ${fmtDate(insurance.dueDate)}`
                    : "The due date is set once the agreement is executed."}
              </Text>
            </Stack>
            <StatusChip tone={!required ? "neutral" : coiTone(status)}>{!required ? "Not required" : statusLabel(status)}</StatusChip>
          </Inline>

          {review.isError && <Stack gap={2}><Text tone="danger">Insurance details are unavailable.</Text><Button variant="secondary" onClick={() => void review.refetch()}>Try again</Button></Stack>}
          {!review.data && !review.isError && <Skeleton count={2} />}

          {insurance && <RowGroup>
            <Row title="Carrier" subtitle={insurance.carrier ?? "Not recorded"} wrap />
            <Row title="Policy number" subtitle={insurance.policyNo ?? "Not recorded"} wrap />
            <Row title="Received" subtitle={insurance.receivedAt ? fmtStamp(insurance.receivedAt) : "Nothing received yet"} wrap />
            <Row title="Document" subtitle={insurance.hasDocument ? "On file — see the Files tab" : "No document on file"} wrap />
          </RowGroup>}
          {insurance?.notes && <Text variant="caption" tone="secondary">{insurance.notes}</Text>}

          {required && !readOnly && <Stack gap={2}>
            <FilePicker label="Upload a certificate" accept="application/pdf,image/jpeg,image/png"
              icon={<Upload aria-hidden size={18} />} loading={uploading}
              maxBytes={10 * 1024 * 1024}
              onTooLarge={() => toast.error("That file is larger than 10 MB. A photo taken at a smaller size will do.")}
              onPick={file => void upload(file)} />
            <Button variant="secondary" disabled={!review.data} onClick={() => setEditingInsurance(true)}>Record carrier, policy and status</Button>
            <Text variant="caption" tone="tertiary">
              A PDF or a photo, up to 10 MB. The client can also upload one from their own page.
            </Text>
          </Stack>}
          {status === "received" && <Text variant="caption" tone="secondary">
            A certificate has arrived. Read it, then record the carrier and policy number.
          </Text>}
        </Stack></Card>
      </Stack>
    </Region>

    <Region area="main" label="Who is working this event">
      <Stack gap={3}>
        <Inline justify="between" align="start" wrap>
          <Stack gap={1}>
            <Text as="h2" variant="title">Who is working this event</Text>
            <Text variant="caption" tone="secondary">Each vendor needs a certificate of their own.</Text>
          </Stack>
          {!readOnly && <Inline gap={2} wrap>
            <Button fullWidth={false} icon={<Plus aria-hidden size={18} />} onClick={() => setPicking(true)}>Add a vendor</Button>
            <Button variant="secondary" fullWidth={false} onClick={() => setAdding(true)}>One-off name</Button>
          </Inline>}
        </Inline>
        {vendors.isError && <Stack gap={2}><Text tone="danger">Vendors could not be loaded.</Text><Button variant="secondary" onClick={() => void vendors.refetch()}>Try again</Button></Stack>}
        {!vendors.data ? <Skeleton count={3} />
          : vendors.data.length === 0
            ? <EmptyState icon={Users} title="No vendors yet" body="Add the caterer, the florist, the band — anybody working the event." />
            : <RowGroup>
              {vendors.data.map(vendor => <Row key={vendor.id}
                icon={<ShieldCheck aria-hidden size={22} />}
                title={vendor.name}
                subtitle={[vendor.category, vendor.role, vendor.arrivalAt ? `arrives ${vendor.arrivalAt}` : null, vendor.contactName, vendor.phone, vendor.email].filter(Boolean).join(" · ") || "No details"}
                wrap
                trailing={<Stack gap={1} align="end">
                  <StatusChip tone={coiTone(vendor.coiStatus)}>{statusLabel(vendor.coiStatus)}</StatusChip>
                  {vendor.stale && <StatusChip tone="warn">Details changed</StatusChip>}
                </Stack>}
                onClick={readOnly ? undefined : () => setEditing(vendor)}
                actions={
                  <>
                    <NavBarAction label={`Call ${vendor.name}`} icon={<Phone aria-hidden size={20} />}
                      disabled={!vendor.phone} disabledReason={vendor.phone ? null : "No phone number"}
                      onClick={() => vendor.phone && window.location.assign(`tel:${vendor.phone}`)} />
                    <NavBarAction label={`Text ${vendor.name}`} icon={<MessageSquare aria-hidden size={20} />}
                      disabled={!vendor.phone} disabledReason={vendor.phone ? null : "No phone number"}
                      onClick={() => vendor.phone && window.location.assign(`sms:${vendor.phone}`)} />
                    <NavBarAction label={`Email ${vendor.name}`} icon={<Mail aria-hidden size={20} />}
                      disabled={!vendor.email} disabledReason={vendor.email ? null : "No email address"}
                      onClick={() => vendor.email && window.location.assign(`mailto:${vendor.email}`)} />
                    {!readOnly && vendor.stale && <NavBarAction label={`Re-sync ${vendor.name} from the directory`}
                      icon={<RefreshCw aria-hidden size={20} />}
                      onClick={() => void resync(vendor)} />}
                  </>
                } />)}
            </RowGroup>}
        {(vendors.data?.some(vendor => vendor.coiStatus === "pending" || vendor.coiStatus === "overdue") ?? false) && <Text variant="caption" tone="secondary">
          The client is asked for outstanding vendor certificates 45 and 30 days before the event.
        </Text>}
      </Stack>
    </Region>

    {review.data && <InsuranceSheet key={booking.id} detail={review.data} files={files.data ?? []} open={editingInsurance}
      unavailable={review.isError || files.isError}
      onReload={async () => { const [result] = await Promise.all([review.refetch(), files.refetch()]); return result.isError ? undefined : result.data; }}
      onClose={() => setEditingInsurance(false)} />}
    {picking && <VendorPickerSheet bookingId={booking.id} venueId={booking.venueId ?? null}
      attachedVendorIds={(vendors.data ?? []).map(vendor => vendor.vendorId).filter((id): id is string => Boolean(id))}
      onClose={() => setPicking(false)} />}
    {adding && <VendorSheet bookingId={booking.id} onClose={() => setAdding(false)} />}
    {editing && <VendorSheet key={editing.id} bookingId={booking.id} vendor={editing} onClose={() => setEditing(null)} />}
  </Workspace>;
}

function VendorSheet({ bookingId, vendor, onClose }: { bookingId: string; vendor?: BookingVendor; onClose: () => void }) {
  const toast = useToast();
  const [form, setForm] = useState({
    name: vendor?.name ?? "", category: vendor?.category ?? "", contactName: vendor?.contactName ?? "",
    phone: vendor?.phone ?? "", email: vendor?.email ?? "", role: vendor?.role ?? "",
    arrivalAt: vendor?.arrivalAt ?? "", coiStatus: vendor?.coiStatus ?? "pending", notes: vendor?.notes ?? "",
  });
  const [baseline] = useState(form);
  const [removing, setRemoving] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const saved = useState({ done: false })[0];
  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);

  const payload = {
    name: form.name,
    ...(form.category ? { category: form.category } : {}),
    ...(form.contactName ? { contactName: form.contactName } : {}),
    ...(form.phone ? { phone: form.phone } : {}),
    ...(form.email ? { email: form.email } : {}),
    ...(form.role ? { role: form.role } : {}),
    ...(form.arrivalAt ? { arrivalAt: form.arrivalAt } : {}),
    coiStatus: form.coiStatus as "pending" | "received" | "waived" | "overdue",
    ...(form.notes ? { notes: form.notes } : {}),
  };
  const parsed = parseInput(bookingVendorSave, payload, { type: "booking_vendor", ...(vendor ? { id: vendor.id } : {}) }, "booking_vendor.save");
  const findings = validateBookingVendor({ ...(vendor ? { id: vendor.id } : {}), name: form.name, coiStatus: form.coiStatus, email: form.email || null, phone: form.phone || null }, { checksComplete: false });
  const validation = useFormValidation(evaluation([...(dirty && !parsed.success ? parsed.issues : []), ...findings.issues], false));

  function change<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    validation.edit(() => { saved.done = false; setForm(previous => ({ ...previous, [key]: value })); });
  }

  async function submit() {
    if (!dirty || !parsed.success || !validation.begin("booking_vendor.save")) return;
    try {
      if (vendor) await bookingVendorApi.update(bookingId, vendor.id, payload);
      else await bookingVendorApi.add(bookingId, payload);
      saved.done = true;
      validation.clear();
      toast.success(vendor ? "Vendor saved" : "Vendor added");
      onClose();
    } catch (error) { validation.reject(error); toast.error(errorMessage(error)); }
    finally { validation.finish(); }
  }

  return <>
    <UnsavedChanges dirty={dirty} pending={validation.busy} canLeave={() => saved.done} />
    <BottomSheet open onClose={() => (dirty ? setConfirmingClose(true) : onClose())} title={vendor ? vendor.name : "Add a vendor"}
      footer={<Button loading={validation.busy} disabled={!dirty} onClick={() => void submit()}>{vendor ? "Save vendor" : "Add vendor"}</Button>}>
      <Stack gap={3}>
        <TextField label="Name" value={form.name} onChange={event => change("name", event.target.value)} {...validation.field("name")} />
        <FormGrid>
          <TextField label="What they do" value={form.category} placeholder="Catering" onChange={event => change("category", event.target.value)} {...validation.field("category")} />
          <TextField label="Their part in the day" value={form.role} placeholder="Dinner service" onChange={event => change("role", event.target.value)} {...validation.field("role")} />
        </FormGrid>
        <TextField label="Who to ask for" value={form.contactName} onChange={event => change("contactName", event.target.value)} {...validation.field("contactName")} />
        <FormGrid>
          <TextField label="Phone" type="tel" value={form.phone} onChange={event => change("phone", event.target.value)} {...validation.field("phone")} />
          <TextField label="Email" type="email" value={form.email} onChange={event => change("email", event.target.value)} {...validation.field("email")} />
        </FormGrid>
        <FormGrid>
          <TextField label="Arriving on" type="date" value={form.arrivalAt.slice(0, 10)}
            onChange={event => change("arrivalAt", event.target.value ? `${event.target.value}T${form.arrivalAt.slice(11, 16) || "09:00"}` : "")}
            {...validation.field("arrivalAt")} />
          <TextField label="Arriving at" type="time" value={form.arrivalAt.slice(11, 16)} disabled={!form.arrivalAt}
            onChange={event => change("arrivalAt", form.arrivalAt ? `${form.arrivalAt.slice(0, 10)}T${event.target.value}` : "")} />
        </FormGrid>
        <Picker label="Their certificate" value={form.coiStatus} onChange={value => change("coiStatus", value)}
          options={[{ value: "pending", label: "Not received yet" }, { value: "received", label: "Received" }, { value: "waived", label: "Waived" }, { value: "overdue", label: "Overdue" }]}
          {...validation.field("coiStatus")} />
        <TextArea label="Notes" rows={3} value={form.notes} onChange={event => change("notes", event.target.value)} {...validation.field("notes")} />
        <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />
        {vendor && <Button variant="secondary" onClick={() => setRemoving(true)}>Remove this vendor</Button>}
      </Stack>
    </BottomSheet>
    {/* A sheet's close gesture is a phone's back button; the router guard cannot
        see it, so a half-written record asks before it is discarded. */}
    <BottomSheet open={confirmingClose} onClose={() => setConfirmingClose(false)} title="Keep your changes?"
      footer={<Button onClick={() => setConfirmingClose(false)}>Keep editing</Button>}>
      <Stack gap={3}>
        <Text>You have made changes but not saved them.</Text>
        <Button variant="secondary" onClick={() => { setConfirmingClose(false); onClose(); }}>Discard my changes</Button>
      </Stack>
    </BottomSheet>
    {removing && vendor && <BottomSheet open onClose={() => setRemoving(false)} title="Remove this vendor?"
      footer={<Button loading={validation.busy} onClick={async () => {
        try { await bookingVendorApi.remove(bookingId, vendor.id); toast.success("Vendor removed"); onClose(); }
        catch (error) { toast.error(errorMessage(error)); }
      }}>Remove them</Button>}>
      <Text>{vendor.name} will be taken off this booking.</Text>
    </BottomSheet>}
  </>;
}
